import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowUpIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import ResourcePicker from '../../../components/admin/ResourcePicker';
import RichTextEditor, { RichTextEditorHandle } from '../../../components/admin/RichTextEditor';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import Button from '../../../components/ui/Button';
import Card from '../../../components/ui/Card';
import ErrorState from '../../../components/ui/ErrorState';
import Input from '../../../components/ui/Input';
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton';
import ToastContainer from '../../../components/ui/ToastContainer';
import { useToast } from '../../../hooks/useToast';
import { useUnsavedChanges } from '../../../hooks/useUnsavedChanges';
import { usePageTitle } from '../../../hooks/usePageTitle';
import { hubAPI } from '../../../services/hubApi';
import { programAPI } from '../../../services/programApi';
import { wordpressAPI } from '../../../services/wordpressApi';
import { Hub } from '../../../types/hub';
import { PlaceKind, ProgramHighlight, ProgramSection, ProgramStatus } from '../../../types/program';
import { Topic } from '../../../types/metadata';
import { WPPost } from '../../../types/wordpress';
import { decodeHtmlEntities } from '../../../utils/htmlEntities';
import { hubProseClassName } from '../../../utils/hubProse';
import { countSectionWords, INLINE_SECTION_WORD_LIMIT, INLINE_SECTION_WORD_MESSAGE } from '../../../utils/sectionWords';
import TopicInterestPicker from '../../../components/ui/TopicInterestPicker';

function asTopics(values: string[] | undefined): Topic[] {
  const allowed = new Set<string>(Object.values(Topic));
  return (values ?? []).filter((value): value is Topic => allowed.has(value));
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function SectionWordCount({ body, hasOwnPage }: { body: string; hasOwnPage: boolean }) {
  const count = countSectionWords(body);
  const over = !hasOwnPage && count > INLINE_SECTION_WORD_LIMIT;
  return (
    <p className={`text-sm ${over ? 'text-error' : 'text-gray-500'}`}>
      {hasOwnPage ? `${count} words` : `${count} of ${INLINE_SECTION_WORD_LIMIT} words`}
    </p>
  );
}

export default function ProgramEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [title, setTitle] = useState('');
  usePageTitle(isEditing ? title || 'Edit Program' : 'New Program');
  const [slug, setSlug] = useState('');
  const [introduction, setIntroduction] = useState('');
  const [interestTopics, setInterestTopics] = useState<Topic[]>([]);
  const [kind, setKind] = useState<PlaceKind>('SUBJECT');
  const [status, setStatus] = useState<ProgramStatus>('DRAFT');
  const [sortOrder, setSortOrder] = useState(0);
  const [slugEdited, setSlugEdited] = useState(false);

  const [sections, setSections] = useState<ProgramSection[]>([]);
  const [highlights, setHighlights] = useState<ProgramHighlight[]>([]);
  const [resourcePosts, setResourcePosts] = useState<Map<number, WPPost>>(new Map());
  const [allHubs, setAllHubs] = useState<Hub[]>([]);
  const [programTitles, setProgramTitles] = useState<Map<string, string>>(new Map());
  const [selectedHubIds, setSelectedHubIds] = useState<string[]>([]);
  const [hubQuery, setHubQuery] = useState('');

  const [loading, setLoading] = useState(isEditing);
  const [error, setError] = useState<Error | null>(null);
  const [saving, setSaving] = useState(false);
  const [savingHubs, setSavingHubs] = useState(false);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [sectionDraft, setSectionDraft] = useState({ title: '', body: '' });
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const richTextEditorRef = useRef<RichTextEditorHandle>(null);
  const { toasts, success, error: showError, removeToast } = useToast();

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedData, setLastSavedData] = useState('');

  useUnsavedChanges({ hasUnsavedChanges });

  const loadPosts = async (items: ProgramHighlight[]) => {
    const postsMap = new Map<number, WPPost>();
    await Promise.all(
      items.map(async (highlight) => {
        try {
          const post = await wordpressAPI.fetchPost(highlight.wpPostId);
          postsMap.set(highlight.wpPostId, post);
        } catch (err) {
          console.warn(`Failed to fetch resource post ${highlight.wpPostId}:`, err);
        }
      })
    );
    setResourcePosts(postsMap);
  };

  useEffect(() => {
    if (!isEditing || !id) return;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [program, hubs, programs] = await Promise.all([
          programAPI.fetchProgram(id),
          hubAPI.fetchHubs({ includeDrafts: true }),
          programAPI.fetchPrograms({ includeDrafts: true }),
        ]);
        setTitle(program.title);
        setSlug(program.slug);
        setIntroduction(program.introduction || '');
        setInterestTopics(asTopics(program.interestTopics));
        setKind(program.kind === 'REFERENCE' ? 'REFERENCE' : 'SUBJECT');
        setStatus(program.status);
        setSortOrder(program.sortOrder);
        setSlugEdited(true);
        setSections(program.sections || []);
        setHighlights(program.highlights || []);
        setSelectedHubIds((program.hubs || []).map((hub) => hub.id));
        setAllHubs(hubs);
        setProgramTitles(new Map(programs.map((item) => [item.id, item.title])));
        setLastSavedData(
          JSON.stringify({
            title: program.title,
            slug: program.slug,
            introduction: program.introduction || '',
            interestTopics: asTopics(program.interestTopics),
            kind: program.kind === 'REFERENCE' ? 'REFERENCE' : 'SUBJECT',
            status: program.status,
            sortOrder: program.sortOrder,
          })
        );
        await loadPosts(program.highlights || []);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load program'));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isEditing, id]);

  useEffect(() => {
    if (!isEditing) return;
    const current = JSON.stringify({ title, slug, introduction, interestTopics, kind, status, sortOrder });
    setHasUnsavedChanges(current !== lastSavedData && lastSavedData !== '');
  }, [title, slug, introduction, interestTopics, kind, status, sortOrder, lastSavedData, isEditing]);

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (!slugEdited) setSlug(slugify(value));
  };

  const handleSave = async () => {
    if (!title.trim() || !slug.trim()) {
      showError('Title and slug are required');
      return;
    }
    const details = {
      title: title.trim(),
      slug: slug.trim(),
      introduction: introduction.trim() || null,
      interestTopics: kind === 'REFERENCE' ? [] : interestTopics,
      kind,
      status,
      sortOrder,
    };
    try {
      setSaving(true);
      if (isEditing && id) {
        const updated = await programAPI.updateProgram(id, details);
        setStatus(updated.status);
        setSortOrder(updated.sortOrder);
        setLastSavedData(
          JSON.stringify({
            title: updated.title,
            slug: updated.slug,
            introduction: updated.introduction || '',
            interestTopics: updated.interestTopics ?? [],
            kind: updated.kind === 'REFERENCE' ? 'REFERENCE' : 'SUBJECT',
            status: updated.status,
            sortOrder: updated.sortOrder,
          })
        );
        setHasUnsavedChanges(false);
        success('Program saved');
      } else {
        const created = await programAPI.createProgram({
          ...details,
          introduction: introduction.trim() || undefined,
        });
        success('Program created');
        navigate(`/admin/programs/${created.id}/edit`);
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save program');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateSection = async () => {
    if (!id) return;
    try {
      const section = await programAPI.createSection(id, { title: 'New content section' });
      setSections([...sections, section].sort((a, b) => a.order - b.order));
      setEditingSectionId(section.id);
      setSectionDraft({ title: section.title, body: section.body || '' });
      success('Section added');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to add section');
    }
  };

  const handleSaveSection = async () => {
    if (!id || !editingSectionId) return;
    const current = sections.find((section) => section.id === editingSectionId);
    if (current && !current.slug && countSectionWords(sectionDraft.body) > INLINE_SECTION_WORD_LIMIT) {
      showError(INLINE_SECTION_WORD_MESSAGE);
      return;
    }
    try {
      const updated = await programAPI.updateSection(id, editingSectionId, {
        title: sectionDraft.title.trim() || 'Untitled section',
        body: sectionDraft.body,
      });
      setSections(sections.map((section) => (section.id === updated.id ? updated : section)));
      setEditingSectionId(null);
      success('Section saved');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save section');
    }
  };

  const handleUploadContentImage = async (fileList: FileList | null) => {
    const file = fileList?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showError('Please choose an image file');
      return;
    }
    try {
      setUploadingImage(true);
      const alt = file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ');
      const media = await wordpressAPI.uploadMedia(file, { alt });
      richTextEditorRef.current?.insertImage(media.source_url, media.alt_text || alt);
      success('Image uploaded and inserted into the section');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const handleDeleteSection = async (sectionId: string) => {
    if (!id) return;
    if (!confirm('Delete this section?')) return;
    try {
      await programAPI.deleteSection(id, sectionId);
      setSections(sections.filter((section) => section.id !== sectionId));
      if (editingSectionId === sectionId) setEditingSectionId(null);
      success('Section deleted');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete section');
    }
  };

  const handleMoveSection = async (sectionId: string, direction: 'up' | 'down') => {
    if (!id) return;
    const ordered = [...sections].sort((a, b) => a.order - b.order);
    const index = ordered.findIndex((section) => section.id === sectionId);
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (index < 0 || newIndex < 0 || newIndex >= ordered.length) return;
    const swapped = [...ordered];
    [swapped[index], swapped[newIndex]] = [swapped[newIndex], swapped[index]];
    setSections(swapped.map((section, order) => ({ ...section, order })));
    try {
      const updated = await programAPI.reorderSections(id, swapped.map((section) => section.id));
      setSections(updated);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to reorder sections');
    }
  };

  const toggleHub = (hubId: string) => {
    setSelectedHubIds((current) =>
      current.includes(hubId) ? current.filter((item) => item !== hubId) : [...current, hubId]
    );
  };

  const moveSelectedHub = (hubId: string, direction: 'up' | 'down') => {
    setSelectedHubIds((current) => {
      const index = current.indexOf(hubId);
      const nextIndex = direction === 'up' ? index - 1 : index + 1;
      if (index < 0 || nextIndex < 0 || nextIndex >= current.length) return current;
      const next = [...current];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next;
    });
  };

  const handleSaveHubs = async () => {
    if (!id) return;
    const moving = allHubs.filter(
      (hub) => selectedHubIds.includes(hub.id) && hub.programId && hub.programId !== id
    );
    if (moving.length > 0) {
      const names = moving.map((hub) => hub.title).join(', ');
      if (!confirm(`${names} will move off ${moving.length === 1 ? 'its current program' : 'their current programs'}. Continue?`)) {
        return;
      }
    }
    try {
      setSavingHubs(true);
      const updated = await programAPI.assignHubs(id, selectedHubIds);
      setSelectedHubIds(updated.hubs.map((hub) => hub.id));
      setAllHubs((current) =>
        current.map((hub) => {
          if (selectedHubIds.includes(hub.id)) return { ...hub, programId: id };
          if (hub.programId === id) return { ...hub, programId: null };
          return hub;
        })
      );
      success('Hubs saved');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to assign hubs');
    } finally {
      setSavingHubs(false);
    }
  };

  const handleAddHighlight = async (post: WPPost) => {
    if (!id) return;
    try {
      const highlight = await programAPI.addHighlight(id, post.id);
      setHighlights([...highlights, highlight].sort((a, b) => a.order - b.order));
      setResourcePosts((current) => new Map(current).set(post.id, post));
      success('Highlighted resource added');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to add highlighted resource');
    }
  };

  const handleDeleteHighlight = async (highlightId: string) => {
    if (!id) return;
    try {
      await programAPI.deleteHighlight(id, highlightId);
      setHighlights(highlights.filter((highlight) => highlight.id !== highlightId));
      success('Highlighted resource removed');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to remove highlighted resource');
    }
  };

  const handleMoveHighlight = async (highlightId: string, direction: 'up' | 'down') => {
    if (!id) return;
    const ordered = [...highlights].sort((a, b) => a.order - b.order);
    const index = ordered.findIndex((highlight) => highlight.id === highlightId);
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (index < 0 || newIndex < 0 || newIndex >= ordered.length) return;
    const swapped = [...ordered];
    [swapped[index], swapped[newIndex]] = [swapped[newIndex], swapped[index]];
    setHighlights(swapped.map((highlight, order) => ({ ...highlight, order })));
    try {
      const updated = await programAPI.reorderHighlights(
        id,
        swapped.map((highlight) => highlight.id)
      );
      setHighlights(updated);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to reorder highlighted resources');
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <LoadingSkeleton lines={10} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <ErrorState error={error} title="Failed to Load Program" onRetry={() => window.location.reload()} />
      </div>
    );
  }

  const orderedSections = [...sections].sort((a, b) => a.order - b.order);
  const orderedHighlights = [...highlights].sort((a, b) => a.order - b.order);
  const hubFilter = hubQuery.trim().toLowerCase();
  const visibleHubs = allHubs
    .filter((hub) => {
      if (!hubFilter) return true;
      return hub.title.toLowerCase().includes(hubFilter) || hub.slug.toLowerCase().includes(hubFilter);
    })
    .sort((a, b) => a.title.localeCompare(b.title));

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <ToastContainer toasts={toasts} onClose={removeToast} />
      <Breadcrumb
        items={[
          { label: 'Admin', to: '/admin' },
          { label: 'Programs', to: '/admin/programs' },
          { label: isEditing ? 'Edit Program' : 'Create Program' },
        ]}
      />

      <Link
        to="/admin/programs"
        className="mb-6 inline-flex items-center gap-2 text-primary transition-colors hover:text-primary-dark"
      >
        <ArrowLeftIcon className="h-5 w-5" />
        <span>Back to Programs</span>
      </Link>

      <h1 className="mb-6 text-3xl font-bold text-gray-900">
        {isEditing ? 'Edit Program' : 'Create Program'}
      </h1>

      <Card className="mb-8">
        <div className="space-y-4 p-6">
          <Input label="Title" value={title} onChange={(e) => handleTitleChange(e.target.value)} required />
          <Input
            label="Slug"
            value={slug}
            onChange={(e) => {
              setSlugEdited(true);
              setSlug(slugify(e.target.value));
            }}
            required
          />
          <p className="-mt-2 text-sm text-gray-500">Used in the URL: /your-slug or /references/your-slug</p>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Introduction</label>
            <textarea
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              rows={4}
              value={introduction}
              onChange={(e) => setIntroduction(e.target.value)}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Kind</label>
            <select
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              value={kind}
              onChange={(e) => setKind(e.target.value as PlaceKind)}
            >
              <option value="SUBJECT">Program</option>
              <option value="REFERENCE">Reference</option>
            </select>
          </div>
          <TopicInterestPicker
            value={interestTopics}
            onChange={setInterestTopics}
          />
          <p className="-mt-2 text-sm text-gray-500">
            When someone has one of these interests, their home opens this program. On a program, if
            you leave this empty, home and Suggested use the top topics from documents in its
            toolkits. Leave empty for a reference.
          </p>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
            <select
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              value={status}
              onChange={(e) => setStatus(e.target.value as ProgramStatus)}
            >
              <option value="DRAFT">Draft — hidden from public site</option>
              <option value="PUBLISHED">Published — visible on public site</option>
            </select>
          </div>
          <Input
            label="Sort order"
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(Number.parseInt(e.target.value, 10) || 0)}
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => navigate('/admin/programs')}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || !title.trim() || !slug.trim()}>
              {saving ? 'Saving...' : isEditing ? 'Save Details' : 'Create Program'}
            </Button>
          </div>
        </div>
      </Card>

      {isEditing && id && (
        <>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Content sections</h2>
            <Button variant="secondary" onClick={handleCreateSection}>
              <PlusIcon className="mr-1 h-4 w-4" />
              Content
            </Button>
          </div>

          {orderedSections.length === 0 && (
            <p className="mb-8 text-gray-600">Add content sections for the program page. Programs do not have resource groups.</p>
          )}

          <div className="mb-10 space-y-4">
            {orderedSections.map((section, index) => (
              <Card key={section.id}>
                <div className="p-5">
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Content</p>
                      <h3 className="text-lg font-semibold text-gray-900">{section.title}</h3>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        className="!px-2 !py-2"
                        onClick={() => handleMoveSection(section.id, 'up')}
                        disabled={index === 0}
                        aria-label="Move section up"
                      >
                        <ArrowUpIcon className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        className="!px-2 !py-2"
                        onClick={() => handleMoveSection(section.id, 'down')}
                        disabled={index === orderedSections.length - 1}
                        aria-label="Move section down"
                      >
                        <ArrowDownIcon className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        className="!px-3 !py-2"
                        onClick={() => {
                          setEditingSectionId(section.id);
                          setSectionDraft({ title: section.title, body: section.body || '' });
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        className="!px-2 !py-2"
                        onClick={() => handleDeleteSection(section.id)}
                        aria-label="Delete section"
                      >
                        <TrashIcon className="h-4 w-4 text-error" />
                      </Button>
                    </div>
                  </div>

                  {editingSectionId === section.id ? (
                    <div className="space-y-3 border-t border-gray-100 pt-4">
                      <Input
                        label="Section title"
                        value={sectionDraft.title}
                        onChange={(e) => setSectionDraft({ ...sectionDraft, title: e.target.value })}
                      />
                      <div>
                        <input
                          ref={imageInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleUploadContentImage(e.target.files)}
                        />
                        <RichTextEditor
                          key={editingSectionId}
                          ref={richTextEditorRef}
                          label="Body"
                          value={sectionDraft.body}
                          onChange={(body) => setSectionDraft({ ...sectionDraft, body })}
                          onUploadImageClick={() => imageInputRef.current?.click()}
                          uploadingImage={uploadingImage}
                        />
                        <SectionWordCount body={sectionDraft.body} hasOwnPage={Boolean(section.slug)} />
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={handleSaveSection}>Save section</Button>
                        <Button variant="secondary" onClick={() => setEditingSectionId(null)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    section.body && (
                      <div
                        className={`${hubProseClassName} prose-sm border-t border-gray-100 pt-3`}
                        dangerouslySetInnerHTML={{ __html: section.body }}
                      />
                    )
                  )}
                </div>
              </Card>
            ))}
          </div>

          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Hubs</h2>
              <p className="text-sm text-gray-600">A hub can stay unassigned. Assigning it here moves it off any previous program.</p>
            </div>
            <Button onClick={handleSaveHubs} disabled={savingHubs}>
              {savingHubs ? 'Saving...' : 'Save Hubs'}
            </Button>
          </div>

          <Card className="mb-10">
            <div className="space-y-3 p-6">
              {selectedHubIds.length > 0 && (
                <div className="mb-4 space-y-2">
                  <p className="text-sm font-medium text-gray-900">Order on the program page</p>
                  {selectedHubIds.map((hubId, index) => {
                    const hub = allHubs.find((item) => item.id === hubId);
                    if (!hub) return null;
                    return (
                      <div
                        key={hubId}
                        className="flex items-center justify-between gap-3 rounded-md border border-gray-200 px-3 py-2"
                      >
                        <p className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">{hub.title}</p>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            className="!px-2 !py-2"
                            onClick={() => moveSelectedHub(hubId, 'up')}
                            disabled={index === 0}
                            aria-label={`Move ${hub.title} up`}
                          >
                            <ArrowUpIcon className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            className="!px-2 !py-2"
                            onClick={() => moveSelectedHub(hubId, 'down')}
                            disabled={index === selectedHubIds.length - 1}
                            aria-label={`Move ${hub.title} down`}
                          >
                            <ArrowDownIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <Input
                type="text"
                placeholder="Filter hubs by title or slug..."
                value={hubQuery}
                onChange={(e) => setHubQuery(e.target.value)}
              />
              {visibleHubs.length === 0 ? (
                <p className="text-sm text-gray-600">No hubs match.</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {visibleHubs.map((hub) => {
                    const checked = selectedHubIds.includes(hub.id);
                    const otherProgram =
                      hub.programId && hub.programId !== id ? programTitles.get(hub.programId) : null;
                    return (
                      <li key={hub.id}>
                        <label className="flex min-h-[44px] items-start gap-3 py-3 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            className="mt-0.5 h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary"
                            checked={checked}
                            onChange={() => toggleHub(hub.id)}
                          />
                          <span>
                            <span className="font-medium text-gray-900">{hub.title}</span>
                            {hub.status === 'DRAFT' && (
                              <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                                Draft
                              </span>
                            )}
                            {otherProgram && (
                              <span className="mt-0.5 block text-gray-500">
                                On {otherProgram}. Saving moves it here.
                              </span>
                            )}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </Card>

          <h2 className="mb-4 text-xl font-semibold text-gray-900">Highlighted resources</h2>
          <Card className="mb-8">
            <div className="p-6">
              <ResourcePicker
                excludeIds={highlights.map((highlight) => highlight.wpPostId)}
                onSelect={handleAddHighlight}
              />
              <div className="mt-4 space-y-2">
                {orderedHighlights.length === 0 && (
                  <p className="text-sm text-gray-600">No highlighted resources yet.</p>
                )}
                {orderedHighlights.map((highlight, index) => {
                  const post = resourcePosts.get(highlight.wpPostId);
                  return (
                    <div
                      key={highlight.id}
                      className="flex items-center justify-between gap-3 rounded-md border border-gray-200 px-3 py-2"
                    >
                      <p className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">
                        {decodeHtmlEntities(post?.title.rendered) || `Resource ${highlight.wpPostId}`}
                      </p>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          className="!px-2 !py-2"
                          onClick={() => handleMoveHighlight(highlight.id, 'up')}
                          disabled={index === 0}
                          aria-label="Move highlighted resource up"
                        >
                          <ArrowUpIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          className="!px-2 !py-2"
                          onClick={() => handleMoveHighlight(highlight.id, 'down')}
                          disabled={index === orderedHighlights.length - 1}
                          aria-label="Move highlighted resource down"
                        >
                          <ArrowDownIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          className="!px-2 !py-2"
                          onClick={() => handleDeleteHighlight(highlight.id)}
                          aria-label="Remove highlighted resource"
                        >
                          <TrashIcon className="h-4 w-4 text-error" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
